import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useState } from "react";
import { format, fromUnixTime } from "date-fns";
import { useSelector } from "react-redux";

import useTheme from "hooks/useTheme";
import useNotification from "hooks/useNotification";

import { findInObjectArrayByMatchingProperties } from "helpers/functions";

import TxDoneIcon from "theme/icons/TxDone";
import CopyIcon from "theme/icons/Copy";
import CalendarIcon from "theme/icons/Calendar";
import TAccountsIcon from "theme/icons/TAccounts";
import TransferIcon from "theme/icons/Transfer";
import FeesIcon from "theme/icons/Fees";
import CoinsIcon from "theme/icons/Coins";
import UserIconRoundIcon from "theme/icons/UserIconRound";
import CloseIcon from "theme/icons/Close";
import AlgoExplorerIcon from "theme/icons/AlgoExplorer";

const TransactionPayDetailModal = (props) => {
	const { appLanguage, themeColor } = useTheme();
	const { notify } = useNotification();

	const accounts = useSelector((state) => state.Accounts);
	const accountInfo = useSelector((state) => state.AccountInfo);
	const [myAccountName, setMyAccountName] = useState(null);

	const [modalInfo, setModalInfo] = useState(null);

	useEffect(() => {
		setModalInfo(null);
	}, []);

	useEffect(() => {
		if (props?.modalInfo) setModalInfo(props.modalInfo);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props]);

	useEffect(() => {
		if (modalInfo?.sender) GetMyAccountName();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [modalInfo]);

	const GetMyAccountName = () => {
		const accountAddress =
			modalInfo["payment-transaction"]["receiver"] === modalInfo.sender || modalInfo.sender !== accountInfo.address
				? modalInfo["payment-transaction"]["receiver"]
				: modalInfo.sender;

		const matchedAccounts = findInObjectArrayByMatchingProperties(accounts, { "account-address": accountAddress });

		setMyAccountName(matchedAccounts?.[0]?.name);
	};

	return (
		<Transition appear show={props?.isOpen} as={Fragment}>
			<Dialog as="div" className="relative z-10" onClose={() => props?.setIsOpen(false)}>
				<Transition.Child
					as={Fragment}
					enter="ease-out duration-300"
					enterFrom="opacity-0"
					enterTo="opacity-100"
					leave="ease-in duration-200"
					leaveFrom="opacity-100"
					leaveTo="opacity-0"
				>
					<div className={`fixed inset-0 ${themeColor?.bg} bg-opacity-30`} />
				</Transition.Child>

				<div className="fixed inset-0 overflow-y-auto">
					<div className="flex min-h-full items-center justify-center p-4 text-center">
						<Transition.Child
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0 scale-95"
							enterTo="opacity-100 scale-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100 scale-100"
							leaveTo="opacity-0 scale-95"
						>
							<Dialog.Panel className="w-full max-w-sm transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
								<div className="flex flex-col w-full items-center space-y-3">
									<div className="flex w-full items-center justify-between">
										<p className={`text-md ${themeColor?.text}`}>{appLanguage?.transactionDetails}</p>
										<CloseIcon
											className="w-4 cursor-pointer"
											fill={themeColor?.hex}
											onClick={() => props?.setIsOpen(false)}
										/>
									</div>
									{modalInfo?.id && (
										<div className="flex w-full items-center justify-start space-x-5">
											<div className="flex w-full items-center space-x-3">
												<TxDoneIcon className="w-8" fill={themeColor?.hex} />
												<div className="flex flex-col">
													<p className={`text-sm ${themeColor?.text}`}>Tx #</p>
													<p className={`text-xs ${themeColor?.text}`}>
														{modalInfo.id.slice(0, 5) + "..." + modalInfo.id.slice(-5)}
													</p>
												</div>
											</div>
											<div className="flex items-center justify-end space-x-3">
												<CopyIcon
													className="w-4 cursor-pointer"
													fill={themeColor?.hex}
													onClick={() => {
														navigator.clipboard.writeText(modalInfo?.id);
														notify("success", "Copied to clipboard");
													}}
												/>
												<AlgoExplorerIcon
													className={`w-4 cursor-pointer`}
													fill={themeColor?.hex}
													onClick={() =>
														window.open(`https://algoexplorer.io/tx/${modalInfo?.id}`, "_blank")
													}
												/>
											</div>
										</div>
									)}

									<div
										className={`flex flex-col w-full items-center justify-center space-y-3 rounded-lg p-3 border ${themeColor?.border}`}
									>
										<div className="flex w-full items-center justify-between">
											<div className="flex w-fit space-x-2">
												<CalendarIcon className={`w-4`} fill={themeColor?.hex} />
												<p className={`text-md ${themeColor?.text}`}>{appLanguage?.date}</p>
											</div>
											<p className={`text-sm ${themeColor?.text}`}>
												{modalInfo?.["round-time"] &&
													format(fromUnixTime(modalInfo["round-time"]), "MMM dd, yyyy kk:mm:ss")}
											</p>
										</div>
										<div className="flex w-full items-center justify-between">
											<div className="flex w-fit space-x-2">
												<TAccountsIcon className={`w-4`} fill={themeColor?.hex} />
												<p className={`text-md ${themeColor?.text}`}>{appLanguage?.myAccount}</p>
											</div>
											<p className={`text-sm ${themeColor?.text}`}>{myAccountName}</p>
										</div>
										<div className="flex w-full items-center justify-between">
											<div className="flex w-fit space-x-2">
												<TransferIcon className={`w-4`} fill={themeColor?.hex} />
												<p className={`text-md ${themeColor?.text}`}>{appLanguage?.type}</p>
											</div>
											<p className={`text-sm ${themeColor?.text}`}>
												{/* {accountInfo.address} */}
												{modalInfo?.["payment-transaction"]?.["receiver"] === modalInfo?.sender ||
												modalInfo?.sender !== accountInfo?.address
													? "Received"
													: "Sent"}
											</p>
										</div>
										<div className="flex w-full items-center justify-between">
											<div className="flex w-fit space-x-2">
												<FeesIcon className={`w-4`} fill={themeColor?.hex} />
												<p className={`text-md ${themeColor?.text}`}>{appLanguage?.fees}</p>
											</div>
											<p className={`text-sm ${themeColor?.text}`}>
												{modalInfo?.fee && modalInfo.fee / 1000000}
											</p>
										</div>
										<div className="flex w-full items-center justify-between">
											<div className="flex w-fit space-x-2">
												<CoinsIcon className={`w-4`} fill={themeColor?.hex} />
												<p className={`text-md ${themeColor?.text}`}>Amount</p>
											</div>
											{modalInfo?.["payment-transaction"]?.["receiver"] === modalInfo?.sender ||
											modalInfo?.sender !== accountInfo?.address ? (
												<p className={`text-sm ${themeColor?.text}`}>
													{modalInfo?.["payment-transaction"]?.["amount"] &&
														modalInfo["payment-transaction"]["amount"] / 1000000}{" "}
													Algo
												</p>
											) : (
												<p className={`text-sm text-candy-red`}>
													-
													{modalInfo?.["payment-transaction"]?.["amount"] &&
														modalInfo["payment-transaction"]["amount"] / 1000000}{" "}
													Algo
												</p>
											)}
										</div>
										<div className="flex w-full items-center justify-between">
											<div className="flex w-fit space-x-2">
												<UserIconRoundIcon className={`w-4`} fill={themeColor?.hex} />
												<p className={`text-md ${themeColor?.text}`}>
													{modalInfo?.["payment-transaction"]?.receiver === modalInfo?.sender ||
													modalInfo?.sender !== accountInfo?.address
														? "From"
														: "To"}
												</p>
											</div>
											<div className="flex items-center justify-end space-x-3">
												<p className={`text-sm ${themeColor?.text}`}>
													{modalInfo?.["payment-transaction"]?.receiver === modalInfo?.sender ||
													modalInfo?.sender !== accountInfo?.address
														? modalInfo?.sender &&
														  modalInfo.sender.slice(0, 5) + "..." + modalInfo.sender.slice(-5)
														: modalInfo?.["payment-transaction"]?.receiver &&
														  modalInfo["payment-transaction"]["receiver"].slice(0, 5) +
																"..." +
																modalInfo["payment-transaction"]["receiver"].slice(-5)}
												</p>
												<CopyIcon
													className="w-4 cursor-pointer"
													fill={themeColor?.hex}
													onClick={() => {
														navigator.clipboard.writeText(
															modalInfo?.["payment-transaction"]?.receiver === modalInfo?.sender ||
																modalInfo?.sender !== accountInfo?.address
																? modalInfo?.sender
																: modalInfo?.["payment-transaction"]?.receiver
														);
														notify("success", "Copied to clipboard");
													}}
												/>
											</div>
										</div>
										<div className="flex flex-col w-full items-start justify-start">
											<div className="flex w-fit space-x-2">
												{/* <UserIconRoundIcon className={`w-4`} fill={themeColor?.hex} /> */}
												<p className={`text-md ${themeColor?.text}`}>{appLanguage?.notes}</p>
											</div>
											<p className={`text-xs break-all`}>{modalInfo?.note && atob(modalInfo.note)}</p>
										</div>
									</div>
								</div>
							</Dialog.Panel>
						</Transition.Child>
					</div>
				</div>
			</Dialog>
		</Transition>
	);
};

export default TransactionPayDetailModal;
